import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';

/**
 * Pure camera-pose math for tour mode. Nothing here touches `Game` or knows
 * which frame (inertial vs. body-fixed) its inputs are expressed in — callers
 * pick the frame by which values they pass in, exactly like the rest of the
 * frame-agnostic math in `src/universe`.
 */

export interface TourPose {
  position: Vec3;
  orientation: Quat;
}

export interface OrbitBasis {
  /** Orbit plane normal. */
  readonly normal: Vec3;
  readonly u: Vec3;
  readonly v: Vec3;
}

/** Deterministic angle in [0, TAU) from a body id, so tours don't all start looking the same way. */
export function seedAngleFromId(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 0xffffffff * Math.PI * 2;
}

/**
 * An orthonormal basis for a circular orbit plane, tilted off the body's spin
 * axis by `inclinationRad` and rotated to a per-body starting longitude by
 * `seedAngle` so every stop's orbit does not trace the same great circle.
 */
export function orbitBasis(spinAxis: Vec3, inclinationRad: number, seedAngle: number): OrbitBasis {
  const tiltAxis = Vec3.anyPerpendicular(spinAxis);
  const tilted = Quat.fromAxisAngle(tiltAxis, inclinationRad).rotate(spinAxis).normalize();
  const u = Quat.fromAxisAngle(tilted, seedAngle).rotate(Vec3.anyPerpendicular(tilted)).normalize();
  const v = Vec3.crossVectors(tilted, u).normalize();
  return { normal: tilted, u, v };
}

/** Camera pose on a circle of `radius` at `angle` around the basis origin, looking at the centre. */
export function orbitPose(radius: number, angle: number, basis: OrbitBasis): TourPose {
  const position = basis.u
    .clone()
    .mulScalar(Math.cos(angle) * radius)
    .addScaled(basis.v, Math.sin(angle) * radius);
  const forward = position.clone().negate().normalize();
  const orientation = Quat.lookRotation(forward, basis.normal);
  return { position, orientation };
}

/**
 * Orbit pose spiralling between two radii while sweeping through an angle
 * span. Angle and radius take separate parameters rather than one shared
 * `t` — used only to lay down control points for `splinePosition` now (the
 * spline is what actually shapes the camera's motion), so there is no
 * reason to force a particular pacing between them here.
 */
export function spiralPose(
  radiusStart: number,
  radiusEnd: number,
  angleStart: number,
  angleSpan: number,
  angleT: number,
  radiusT: number,
  basis: OrbitBasis,
): TourPose {
  const radius = radiusStart + (radiusEnd - radiusStart) * radiusT;
  const angle = angleStart + angleSpan * angleT;
  return orbitPose(radius, angle, basis);
}

/**
 * Catmull-Rom spline through `points`, uniform in control-point index (`t`
 * spans [0, 1] across the whole polyline, not arc length). Endpoints are
 * clamped by reusing the first/last point as the missing neighbour, the
 * standard non-closed-curve treatment.
 *
 * This is the fix for hard direction changes between tour phases: rather
 * than each phase (transit, orbit, descent) computing its pose from an
 * independent formula with its own, unmatched derivative at the boundary,
 * the camera's whole space-side path through a stop is one polyline handed
 * to this function. Catmull-Rom tangents are shared between adjoining
 * segments by construction, so the curve is C1-continuous through every
 * control point — there is no seam left to be hard about.
 */
export function splinePosition(points: Vec3[], t: number): Vec3 {
  if (points.length === 1) return points[0].clone();
  const segments = points.length - 1;
  const scaled = Math.min(Math.max(t, 0), 1) * segments;
  const i = Math.min(Math.floor(scaled), segments - 1);
  const localT = scaled - i;

  const p0 = points[Math.max(0, i - 1)];
  const p1 = points[i];
  const p2 = points[i + 1];
  const p3 = points[Math.min(points.length - 1, i + 2)];

  const t2 = localT * localT;
  const t3 = t2 * localT;
  const c0 = -0.5 * t3 + t2 - 0.5 * localT;
  const c1 = 1.5 * t3 - 2.5 * t2 + 1;
  const c2 = -1.5 * t3 + 2 * t2 + 0.5 * localT;
  const c3 = 0.5 * t3 - 0.5 * t2;

  return new Vec3(
    p0.x * c0 + p1.x * c1 + p2.x * c2 + p3.x * c3,
    p0.y * c0 + p1.y * c1 + p2.y * c2 + p3.y * c3,
    p0.z * c0 + p1.z * c1 + p2.z * c2 + p3.z * c3,
  );
}

/**
 * A low pass over the surface: a great-circle arc at constant altitude, nose
 * pointed along the direction of travel with local "up" as the roll
 * reference. Expressed in whatever frame `startDirection` and `axis` are
 * given in — the surface pass uses the body-fixed frame so the path holds
 * still over the ground rather than sliding with the body's rotation.
 */
export function surfaceFlybyPose(
  bodyRadius: number,
  altitude: number,
  startDirection: Vec3,
  axis: Vec3,
  sweepRad: number,
  t01: number,
): TourPose {
  const p0 = startDirection.normalized();
  const p1 = Vec3.crossVectors(axis, p0).normalize();
  const phi = sweepRad * t01;
  const dir = p0.clone().mulScalar(Math.cos(phi)).addScaled(p1, Math.sin(phi)).normalize();
  const tangent = p0.clone().mulScalar(-Math.sin(phi)).addScaled(p1, Math.cos(phi)).normalize();
  const position = dir.clone().mulScalar(bodyRadius + altitude);
  const orientation = Quat.lookRotation(tangent, dir);
  return { position, orientation };
}
